import {
  DateFormat,
  EventCard,
  EventCardProps,
  getAgeFromDob,
} from '@equip.health/ui';
import { DateTime } from 'luxon';
import { capitalizeFirstLetter } from '~/lib/common';
import {
  formatDateWithTimezone,
  getAttendeePronouns,
  getRecurrenceDisplay,
  Recurrence,
} from '~/lib/util/calendar.util';
import { getPreferredFullName } from '~/lib/util/patient.util';
import { getTimezoneAbbreviation } from '~/lib/util/timezone.util';
import { Path } from '~/routes/constants';

interface AppointmentSummaryProps {
  patient: Patient;
  lovedOne?: PatientContact;
  provider: GetProviderByIdResponse;
  startTime: string;
  endTime: string;
  timeZone: string;
}

const formatTime = (date: string, timeZone: string): string =>
  DateTime.fromISO(date, { zone: timeZone })
    .toFormat(DateFormat.timeWithoutSpace)
    .toLowerCase();

const getAttendeeName = (person: {
  chosenName?: string;
  firstName?: string;
  lastName?: string;
}): string =>
  getPreferredFullName(
    capitalizeFirstLetter(person?.chosenName),
    capitalizeFirstLetter(person?.firstName),
    capitalizeFirstLetter(person?.lastName),
  );

const AppointmentSummary = ({
  provider,
  startTime,
  endTime,
  patient,
  lovedOne,
  timeZone,
}: AppointmentSummaryProps) => {
  const getCredentials = (provider: GetProviderByIdResponse) => {
    if (
      !provider?.credentialsDetails ||
      provider?.credentialsDetails.length === 0
    ) {
      return undefined;
    }
    return provider.credentialsDetails.map((each) => each.name);
  };

  const handleViewPatientProfileClick = (patientId: string): string => {
    return `${window.location.origin}${Path.MAUD_PATIENTS}/${patientId}`;
  };

  const eventCardProps: EventCardProps = {
    eventType: 'appointment',
    scheduleItem: {
      attendees: [
        {
          avatarUrl: provider?.avatar?.url,
          credentials: getCredentials(provider),
          externalId: provider.userId,
          isAttended: false,
          isHost: true,
          name: getAttendeeName(provider),
          pronouns: getAttendeePronouns(provider?.pronouns),
          userType: provider?.providerTypeDetails?.name,
        },
        {
          avatarUrl: patient.avatarSrc,
          externalId: patient.userId,
          isAttended: false,
          isHost: false,
          name: getAttendeeName(patient),
          pronouns: getAttendeePronouns(patient?.pronounsOnRecord),
          userType: 'Patient',
        },
        ...(lovedOne
          ? [
              {
                externalId: lovedOne.contactExternalId,
                isAttended: false,
                isHost: false,
                name: getAttendeeName(lovedOne),
                userType: lovedOne.relationToPatient?.trim() || 'Inquirer',
              },
            ]
          : []),
      ],
      eventDate: formatDateWithTimezone(
        startTime,
        `${DateFormat.weekdayShortMonthDateNoSpecialChar}`,
        timeZone,
      ),
      eventExternalId: '',
      eventName: `Admission consultation, 30 minutes`,
      eventTime: `${formatTime(startTime, timeZone)} - ${formatTime(endTime, timeZone)} ${getTimezoneAbbreviation(timeZone, startTime)}`,
      frequencyText: getRecurrenceDisplay(
        startTime,
        Recurrence.DOES_NOT_REPEAT,
        DateTime.fromISO(startTime).plus({ months: 6, days: -1 }).toISODate(),
        true,
      ),
      patient: {
        age: getAgeFromDob(patient?.dateOfBirth),
        name: getAttendeeName(patient),
      },
      shouldShowViewPatientProfileButton: true,
      shouldShowZoomButton: false,
    },
    patientProfileLink: handleViewPatientProfileClick(patient.userId),
  };

  return <EventCard {...eventCardProps} />;
};

export default AppointmentSummary;
